declare const __LVGL_EDITOR_VERSION__: string | undefined;

import { TheiaApi, type TheiaApiDelegates } from '@lved/api-gateway/dist/implementations/theia.js';
import {
    joinFsPath,
    isValidCIdentifier,
    isValidCIdentifierFilePath,
    LVGLSchemaService,
    LVGL_LOG,
    LicenseService,
    MessageType,
    normalizeFilePathForComparison,
    normalizeWindowsDrivePath,
    getProjectTemplateDefaultFolderName,
    resolveLvglProjectDirectory,
    XML_FILE_EXTENSION,
    VALID_C_VAR_NAME
} from '@lved/shared-core';
import type {
    AuthState,
    CreateProjectOptions,
    GitWorkspaceInfo,
    OpenSaveDialogOptions,
    ProjectTemplateId,
    RecentProject,
    ResolvedLvglProjectDirectory
} from '@lved/shared-core';
import type { FSNode } from '@lved/shared-core/types/fs-types';
import type { DirectoryData, Result } from '@lved/shared-core/types/window-types';
import { XmlSchemaContribution } from '@theia-lved-core/browser/editor/xml-schema-contribution';
import { ONLINE_PREVIEW_SHOW_LAUNCHER_EVENT } from '@theia-lved-core/browser/online-preview/online-preview-host-widget';
import { TheiaAuthStoragePath, type ITheiaAuthStorageServer } from '@theia-lved-core/common/auth-storage-protocol';
import {
    type CLIArgs,
    TheiaProjectServicePath,
    type ITheiaProjectServiceServer,
    type ProjectServiceEvent,
    type ProjectServiceMessageBoxOptions
} from '@theia-lved-core/common/project-service-protocol';
import { TheiaProjectServiceWatcher } from '@theia-lved-core/common/project-service-watcher';
import { isE2EAuthBypassBuild } from '@theia-lved-core/common/build-env';
import {
    createInvalidFileOrDirectoryNameResult,
    INVALID_FILE_OR_DIRECTORY_VARIABLE_NAME_MESSAGE
} from './file-operation-messages';
import { OS, RpcProxy } from '@theia/core';
import { CommandRegistry } from '@theia/core/lib/common/command';
import { DebugCommands } from '@theia/debug/lib/browser/debug-commands';
import { DebugConfigurationManager } from '@theia/debug/lib/browser/debug-configuration-manager';
import { DebugSessionManager } from '@theia/debug/lib/browser/debug-session-manager';
import { DebugSessionOptions } from '@theia/debug/lib/browser/debug-session-options';
import { DebugConfiguration } from '@theia/debug/lib/common/debug-common';
import {
    ApplicationShell,
    ConfirmDialog,
    ConfirmSaveDialog,
    FrontendApplicationContribution,
    open,
    OpenerService,
    StorageService,
    WebSocketConnectionProvider
} from '@theia/core/lib/browser';
import { FrontendApplicationStateService } from '@theia/core/lib/browser/frontend-application-state';
import { BinaryBuffer } from '@theia/core/lib/common/buffer';
import { EnvVariablesServer } from '@theia/core/lib/common/env-variables';
import { Disposable, DisposableCollection } from '@theia/core/lib/common/disposable';
import { FileChangeSuppressionService } from './file-change-suppression-service';
import { MessageService } from '@theia/core/lib/common/message-service';
import type { Progress } from '@theia/core/lib/common/message-service-protocol';
import { PreferenceScope } from '@theia/core/lib/common/preferences/preference-scope';
import {
    PreferenceService,
    PreferenceService as PreferenceServiceSymbol
} from '@theia/core/lib/common/preferences/preference-service';
import URI from '@theia/core/lib/common/uri';
import { inject, injectable } from '@theia/core/shared/inversify';
import type { Widget } from '@theia/core/lib/browser/widgets';
import { FileDialogService } from '@theia/filesystem/lib/browser/file-dialog/file-dialog-service';
import { FileService, TextFileContent } from '@theia/filesystem/lib/browser/file-service';
import { FileChangesEvent, FileContent, FileOperationEvent, FileStat } from '@theia/filesystem/lib/common/files';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';
import { WorkspaceTrustService } from '@theia/workspace/lib/browser/workspace-trust-service';
import { WORKSPACE_TRUST_TRUSTED_FOLDERS } from '@theia/workspace/lib/common/workspace-trust-preferences';
import { NavigationLocationService } from '@theia/editor/lib/browser/navigation/navigation-location-service';
import { useFileStore } from '@lved/ui/store/fileStore';
import { OutputWidget } from '@theia/output/lib/browser/output-widget';

/** Resolved when `installPlatformApi` finishes; backs `TheiaPlatformApiContribution.apiReady`. */
let resolvePlatformApiReady!: (api: TheiaApi) => void;
const platformApiReadyPromise: Promise<TheiaApi> = new Promise<TheiaApi>((resolve) => {
    resolvePlatformApiReady = resolve;
});
const DIRECTORY_REFRESH_DEBOUNCE_MS: number = 100;
const SCHEMA_HOT_RELOAD_DEBOUNCE_MS: number = 250;
const NOTIFICATION_TIMEOUT_MS: number = 3500;
const RUNTIME_RESOURCES_MANIFEST_FILE_NAME: string = 'resources-manifest.json';
const THEIA_FILE_OPERATION_CREATE: number = 0;
const THEIA_FILE_OPERATION_DELETE: number = 1;
const THEIA_FILE_OPERATION_MOVE: number = 2;
const LVGL_RECENT_PROJECTS_STORAGE_KEY: string = 'lvgl.recentProjects';
const MAX_RECENT_PROJECTS: number = 10;
const SYSTEM_DIRECTORIES: ReadonlySet<string> = new Set<string>(['images', 'fonts']);
const PRESERVED_BOTTOM_PANEL_WIDGET_IDS: ReadonlySet<string> = new Set<string>([
    OutputWidget.ID,
    'widget:lvgl-subjects',
    'widget:lvgl-translations',
    'widget:lvgl-tests',
    'widget:lvgl-animations'
]);

type LaunchConfigurationFile = {
    configurations: DebugConfiguration[];
};
type OutputLogSource = 'lvgl' | 'export' | 'runtime';
type RuntimeResourcesManifest = Record<string, FSNode[]>;
type InPlaceWorkspaceService = {
    setWorkspace(workspaceStat: FileStat | undefined): Promise<void>;
    closeInCurrentWindow?: () => Promise<void>;
    opened: boolean;
};
type StoredRecentProject = {
    path: string;
    lastOpened: string;
};
type LvglFileEvent = 'add' | 'change' | 'unlink';

const LVGL_SCHEMA_HOT_RELOAD_COMMAND_ID: string = 'lvgl.schema.hotReload';

@injectable()
export class TheiaPlatformApiContribution implements FrontendApplicationContribution {
    protected static readonly BROWSER_RESOURCES_ROOT = '/@lved-runtime-resources';

    @inject(FileService)
    protected readonly fileService!: FileService;

    @inject(MessageService)
    protected readonly messageService!: MessageService;

    @inject(FrontendApplicationStateService)
    protected readonly appStateService!: FrontendApplicationStateService;

    @inject(OpenerService)
    protected readonly openerService!: OpenerService;

    @inject(WebSocketConnectionProvider)
    protected readonly connectionProvider!: WebSocketConnectionProvider;

    @inject(TheiaProjectServiceWatcher)
    protected readonly projectServiceWatcher!: TheiaProjectServiceWatcher;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    @inject(WorkspaceTrustService)
    protected readonly workspaceTrustService!: WorkspaceTrustService;

    @inject(PreferenceServiceSymbol)
    protected readonly preferences!: PreferenceService;

    @inject(NavigationLocationService)
    protected readonly navigationLocationService!: NavigationLocationService;

    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    @inject(StorageService)
    protected readonly storageService!: StorageService;

    @inject(FileDialogService)
    protected readonly fileDialogService!: FileDialogService;

    @inject(EnvVariablesServer)
    protected readonly envVariablesServer!: EnvVariablesServer;

    @inject(XmlSchemaContribution)
    protected readonly xmlSchemaContribution!: XmlSchemaContribution;

    @inject(CommandRegistry)
    protected readonly commandRegistry!: CommandRegistry;

    @inject(FileChangeSuppressionService)
    protected readonly fileChangeSuppressionService!: FileChangeSuppressionService;

    @inject(DebugConfigurationManager)
    protected readonly debugConfigurationManager!: DebugConfigurationManager;

    @inject(DebugSessionManager)
    protected readonly debugSessionManager!: DebugSessionManager;

    protected homePath: string | undefined;

    protected readonly toDispose: DisposableCollection = new DisposableCollection();
    protected readonly debugSessionOptionsByWorkspace: Map<string, DebugSessionOptions> = new Map<
        string,
        DebugSessionOptions
    >();
    protected api: TheiaApi | undefined;
    protected projectService: RpcProxy<ITheiaProjectServiceServer> | undefined;
    protected authStorage: RpcProxy<ITheiaAuthStorageServer> | undefined;
    protected directoryRefreshTimeout: ReturnType<typeof setTimeout> | undefined;
    protected schemaHotReloadTimeout: ReturnType<typeof setTimeout> | undefined;
    protected runtimeResourcesManifestPromise: Promise<RuntimeResourcesManifest | null> | undefined;
    protected readonly projectServiceProgressByKey = new Map<string, Progress>();
    protected readonly projectServiceProgressStarting = new Map<string, Promise<Progress>>();

    /** Fulfilled when `installPlatformApi` has assigned `window._platformApi`. */
    readonly apiReady: Promise<TheiaApi> = platformApiReadyPromise;

    protected schemaService: LVGLSchemaService | undefined;

    async onStart(): Promise<void> {
        this.installPlatformGlobals();
        this.installPlatformApi();
        if (!this.api) {
            throw new Error('[Theia-API] Platform API missing after installPlatformApi');
        }
        resolvePlatformApiReady(this.api);

        this.attachProjectServiceMessageBoxHandler();
        this.attachProjectServiceEventLogger();
        this.attachFileChangeBridge();
        this.attachFileOperationBridge();
        this.attachWorkspaceSchemaInitializer();
        this.registerSchemaHotReloadCommand();
        this.registerDebugStartFallbacks();
        this.toDispose.push(Disposable.create(() => this.clearDirectoryRefreshTimeout()));
        this.toDispose.push(Disposable.create(() => this.clearSchemaHotReloadTimeout()));

        await this.workspaceService.ready;
        await this.trustCurrentWorkspaceRoot();
        // Can't directly await openStartupProjectFromCliArgs because it may show a message box,
        // which hangs Theia if you trigger it in an onStart() handler.
        this.appStateService
            .reachedState('ready')
            .then(() => this.openStartupProjectFromCliArgs())
            .then(() => this.initializeSchemaServiceFromCurrentWorkspace());
    }

    protected isBrowserOnlyMode(): boolean {
        return !navigator.userAgent.toLowerCase().includes('electron');
    }

    protected showNotification(message: string, type: MessageType = MessageType.DEFAULT): void {
        if (type === MessageType.ERROR) {
            void this.messageService.error(message, { timeout: NOTIFICATION_TIMEOUT_MS });
            return;
        }
        if (type === MessageType.WARNING) {
            void this.messageService.warn(message, { timeout: NOTIFICATION_TIMEOUT_MS });
            return;
        }
        if (type === MessageType.SUCCESS) {
            this.showSuccessNotification(message);
            return;
        }
        void this.messageService.info(message, { timeout: NOTIFICATION_TIMEOUT_MS });
    }

    /** Theia has no success type; bold markdown is our CSS hook for success accents. */
    protected showSuccessNotification(message: string): void {
        void this.messageService.info(`**${message}**`, { timeout: NOTIFICATION_TIMEOUT_MS });
    }

    protected async initializeSchemaService(projectPath: string): Promise<LVGLSchemaService | undefined> {
        if (!this.api) return undefined;

        if (this.schemaService) {
            return this.reloadSchemaServiceForProject(projectPath);
        }

        try {
            const initialized: LVGLSchemaService | undefined =
                await this.xmlSchemaContribution.initializeFromPlatformApi(this.api, projectPath);
            if (!initialized) {
                console.warn('[Theia-API] Failed to initialize schema service.');
                return undefined;
            }
            this.schemaService = initialized;
            return initialized;
        } catch (error: unknown) {
            console.error('[Theia-API] Failed to initialize schema service', error);
            return undefined;
        }
    }

    protected async reloadSchemaServiceForProject(projectPath: string): Promise<LVGLSchemaService | undefined> {
        try {
            const reloaded: LVGLSchemaService | undefined =
                await this.xmlSchemaContribution.reloadForProject(projectPath);
            if (!reloaded) {
                console.warn('[Theia-API] Failed to reload schema service for new project.');
                return this.schemaService;
            }
            return reloaded;
        } catch (error: unknown) {
            console.error('[Theia-API] Failed to reload schema service for project', error);
            return this.schemaService;
        }
    }

    protected attachWorkspaceSchemaInitializer(): void {
        this.toDispose.push(
            this.workspaceService.onWorkspaceChanged(() => {
                this.initializeSchemaServiceFromCurrentWorkspace();
            }) as unknown as Disposable
        );
    }

    onStop(): void {
        this.clearProjectServiceProgress();
        this.toDispose.dispose();
    }

    protected async initializeSchemaServiceFromCurrentWorkspace(): Promise<LVGLSchemaService | undefined> {
        const projectPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (!projectPath) {
            console.warn(
                '[Theia-API] No workspace root; LVGL schema service will initialize after a project is opened.'
            );
            return undefined;
        }

        return this.initializeSchemaService(projectPath);
    }

    protected registerSchemaHotReloadCommand(): void {
        this.toDispose.push(
            this.commandRegistry.registerCommand(
                { id: LVGL_SCHEMA_HOT_RELOAD_COMMAND_ID },
                {
                    execute: (triggerPath?: unknown): Promise<void> => this.hotReloadCurrentProjectSchema(triggerPath)
                }
            )
        );
    }

    protected async hotReloadCurrentProjectSchema(triggerPath?: unknown): Promise<void> {
        this.clearSchemaHotReloadTimeout();

        const projectPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (!projectPath) {
            console.warn('[Theia-API] Cannot hot-reload LVGL schema without an open workspace.');
            return;
        }

        const normalizedTriggerPath: string =
            typeof triggerPath === 'string' && triggerPath.trim()
                ? this.normalizeProjectPathForFileService(triggerPath)
                : projectPath;

        const readySchemaService: LVGLSchemaService | undefined = await this.getSchemaServiceForHotReload(projectPath);
        if (!readySchemaService) {
            console.warn('[Theia-API] Cannot hot-reload LVGL schema before schema service initialization.');
            return;
        }

        await readySchemaService.hotReloadProject(projectPath, normalizedTriggerPath);
        this.schemaService = readySchemaService;
        this.scheduleWorkspaceDirectoryRefresh(normalizedTriggerPath);
    }

    protected async getSchemaServiceForHotReload(projectPath: string): Promise<LVGLSchemaService | undefined> {
        const existingSchemaService: LVGLSchemaService | undefined =
            this.schemaService ??
            this.xmlSchemaContribution.getSchemaService() ??
            (await this.xmlSchemaContribution.ensureInitialized());
        if (existingSchemaService) return existingSchemaService;
        if (!this.api) return undefined;

        return this.xmlSchemaContribution.initializeFromPlatformApi(this.api, projectPath);
    }

    protected async openStartupProjectFromCliArgs(): Promise<void> {
        const projectService: RpcProxy<ITheiaProjectServiceServer> | undefined = this.getProjectService();
        if (!projectService) return;

        try {
            const { projectPath, renesasConfigPath }: CLIArgs = await projectService.getStartupCliArgs();
            if (!projectPath) return;

            console.log('[Theia-API] Opening startup project from CLI args:', { projectPath, renesasConfigPath });

            if (await this.directoryExists(projectPath)) {
                console.log('[Theia-API] Project directory exists, maybe update from Renesas config if needed');
                // We intentionally do not wait in this, because the popup will be hidden by the launch screen.
                // So this will put up the prompt, then we go ahead and open the project below so the user can
                // see it.
                void this.maybeUpdateProjectConfigFromRenesas(projectService, projectPath, renesasConfigPath).catch(
                    (error: unknown) => {
                        console.warn('[Theia-API] Failed to update project config from Renesas config', error);
                    }
                );
            } else {
                console.log(
                    '[Theia-API] Project directory does not exist, creating new project from Renesas config if available'
                );
                const haveConfig: boolean =
                    renesasConfigPath !== undefined && (await this.fileExists(renesasConfigPath));
                const shouldCreateFromRenesas: boolean = haveConfig
                    ? await this.askYesNo(
                          'Create Project',
                          `Do you want to create a new LVGL project from the Renesas configuration file at "${renesasConfigPath}"?`
                      )
                    : false;

                const created: boolean = await projectService.createProject(projectPath, {
                    componentLib: false
                });
                if (!created) {
                    this.messageService.error(`Failed to create project at ${projectPath}`);
                    return;
                }

                if (shouldCreateFromRenesas && renesasConfigPath) {
                    await projectService.updateProjectConfigFromRenesasConfig(projectPath, renesasConfigPath);
                }
            }

            await this.setWorkspaceInPlace(projectPath);
        } catch (error: unknown) {
            console.warn('[Theia-API] Failed to open startup project from CLI args', error);
        }
    }

    protected async maybeUpdateProjectConfigFromRenesas(
        projectService: RpcProxy<ITheiaProjectServiceServer>,
        projectPath: string,
        renesasConfigPath?: string
    ): Promise<void> {
        if (!renesasConfigPath || !(await this.fileExists(renesasConfigPath))) return;

        const configsDiffer: boolean = await projectService.isRenesasConfigDifferentFromProjectConfig(
            projectPath,
            renesasConfigPath
        );
        if (!configsDiffer) return;

        const shouldUpdate: boolean = await this.askYesNo(
            'Update LVGL Configuration',
            'The Renesas display configuration has changed, do you want to update the LVGL configuration from the Renesas one?'
        );
        if (shouldUpdate) {
            await projectService.updateProjectConfigFromRenesasConfig(projectPath, renesasConfigPath);
        }
    }

    protected async askYesNo(title: string, message: string): Promise<boolean> {
        // Use ConfirmDialog instead of messageService because it must show above the launch screen
        return (
            (await new ConfirmDialog({
                title,
                msg: message,
                ok: 'Yes',
                cancel: 'No'
            }).open()) === true
        );
    }

    protected async directoryExists(dirPath: string): Promise<boolean> {
        try {
            return (await this.fileService.resolve(this.toFileUri(dirPath))).isDirectory;
        } catch {
            return false;
        }
    }

    protected async fileExists(filePath: string): Promise<boolean> {
        try {
            return !(await this.fileService.resolve(this.toFileUri(filePath))).isDirectory;
        } catch {
            return false;
        }
    }

    protected installPlatformGlobals(): void {
        if (!window.platform?.getHostPlatform) {
            window.platform = {
                ...window.platform,
                getHostPlatform: (): string => this.detectHostPlatform()
            };
        }

        window.LVGL_EDITOR_SCHEME = window.LVGL_EDITOR_SCHEME || 'theia';
        window.LVGL_EDITOR_VERSION =
            (typeof __LVGL_EDITOR_VERSION__ !== 'undefined' ? __LVGL_EDITOR_VERSION__ : undefined) || 'theia-dev';
        window.LVGL_THEIA_BROWSER_ONLY = this.isBrowserOnlyMode();
    }

    protected installPlatformApi(): void {
        const delegates: TheiaApiDelegates = {
            getResourcesSourcePath: async (): Promise<string> => this.resolveResourcesSourcePath(),
            getResourcesPath: async (): Promise<string> => this.resolveResourcesPath(),
            getDirectoryData: async (dirPath: string): Promise<DirectoryData | null> => {
                if (this.isBrowserResourcesPath(dirPath)) {
                    return this.getHttpDirectoryData(dirPath);
                }
                const normalizedDirPath: string = this.normalizeProjectPathForFileService(dirPath);
                const stat: FileStat = await this.fileService.resolve(this.toFileUri(normalizedDirPath));
                if (!stat.isDirectory) return null;
                return {
                    dirPath: normalizedDirPath,
                    files: await Promise.all((stat.children ?? []).map((child: FileStat) => this.toFsNode(child)))
                };
            },
            pathIsDirectory: async (dirPath: string): Promise<boolean> => {
                if (this.isBrowserResourcesPath(dirPath)) {
                    return (await this.getHttpDirectoryData(dirPath)) !== null;
                }
                try {
                    const normalizedDirPath: string = this.normalizeProjectPathForFileService(dirPath);
                    const stat: FileStat = await this.fileService.resolve(this.toFileUri(normalizedDirPath));
                    return stat.isDirectory;
                } catch {
                    return false;
                }
            },
            getFileData: async (filePath: string): Promise<string> => {
                if (this.isBrowserResourcesPath(filePath)) {
                    const response: Response = await fetch(filePath);
                    if (!response.ok) {
                        throw new Error(`Failed to read browser resource: ${filePath} (${response.status})`);
                    }
                    return response.text();
                }
                const normalizedFilePath: string = this.normalizeProjectPathForFileService(filePath);
                return this.readTextFile(normalizedFilePath);
            },
            getBinaryFileData: async (filePath: string): Promise<Uint8Array> => {
                if (this.isBrowserResourcesPath(filePath)) {
                    const response: Response = await fetch(filePath);
                    if (!response.ok) {
                        throw new Error(`Failed to read browser resource: ${filePath} (${response.status})`);
                    }
                    return new Uint8Array(await response.arrayBuffer());
                }
                const normalizedFilePath: string = this.normalizeProjectPathForFileService(filePath);
                return this.readBinaryFile(normalizedFilePath);
            },
            saveFileData: async (filePath: string, content: string): Promise<boolean> => {
                const normalizedFilePath: string = this.normalizeProjectPathForFileService(filePath);
                await this.fileService.write(this.toFileUri(normalizedFilePath), content);
                return true;
            },
            writeBinaryFile: async (filePath: string, data: Uint8Array): Promise<void> => {
                const normalizedFilePath: string = this.normalizeProjectPathForFileService(filePath);
                await this.fileService.writeFile(this.toFileUri(normalizedFilePath), BinaryBuffer.wrap(data.slice()));
            },
            delete: async (filePath: string): Promise<void> => {
                const normalizedFilePath: string = this.normalizeProjectPathForFileService(filePath);
                await this.fileService.delete(this.toFileUri(normalizedFilePath), { recursive: true });
            },
            showErrorMessageBox: async (title: string, message: string): Promise<number> => {
                await this.messageService.error(`${title}: ${message}`);
                return 0;
            },
            showNotification: (message: string, type?: MessageType): void => {
                this.showNotification(message, type);
            },
            showUnsavedFilesDialog: async (title: string, message: string): Promise<number> => {
                const result: boolean | undefined = await new ConfirmSaveDialog({
                    title,
                    msg: message,
                    save: 'Save All',
                    dontSave: 'Do Not Save',
                    cancel: 'Cancel'
                }).open();
                if (result === true) return 0;
                if (result === false) return 1;
                return 2;
            },
            showConfirmDialog: async (
                title: string,
                message: string,
                confirmLabel?: string,
                cancelLabel?: string
            ): Promise<boolean> =>
                (await new ConfirmDialog({
                    title,
                    msg: message,
                    ok: confirmLabel ?? 'Yes',
                    cancel: cancelLabel ?? 'Back'
                }).open()) === true,
            openExternal: async (url: string): Promise<void> => {
                await open(this.openerService, new URI(url));
            },
            generateProjectCode: async (projectPath: string): Promise<Result> => {
                if (this.isBrowserOnlyMode()) {
                    // This is a safe guard against the code generation button somehow being enabled in browser-only mode
                    return {
                        success: false,
                        error: 'Code generation is not available in browser-only mode.'
                    };
                }

                try {
                    return await this.getProjectService()!.generateProjectCode(projectPath);
                } catch (error: unknown) {
                    return {
                        success: false,
                        error: error instanceof Error ? error.message : String(error)
                    };
                }
            },
            compileProject: async (projectPath: string): Promise<Result> => {
                if (this.isBrowserOnlyMode()) {
                    return {
                        success: false,
                        error: 'Compile is not available in browser-only mode.'
                    };
                }

                try {
                    return await this.getProjectService()!.compileProject(projectPath);
                } catch (error: unknown) {
                    return {
                        success: false,
                        error: error instanceof Error ? error.message : String(error)
                    };
                }
            },
            cleanProject: async (projectPath: string): Promise<Result> => {
                if (this.isBrowserOnlyMode()) {
                    return {
                        success: false,
                        error: 'Project cleaning is not available in browser-only mode.'
                    };
                }
                if (!projectPath) {
                    return {
                        success: false,
                        error: 'Invalid project folder. Please open a valid project folder.'
                    };
                }

                try {
                    return await this.getProjectService()!.cleanProject(projectPath);
                } catch (error: unknown) {
                    return {
                        success: false,
                        error: error instanceof Error ? error.message : String(error)
                    };
                }
            },
            selectDirectory: async (projectPath?: string): Promise<DirectoryData | null> =>
                this.selectProjectDirectory(projectPath),
            openDirectorySelector: async (defaultPath?: string): Promise<string> =>
                this.openDestinationDirectorySelector(defaultPath),
            openSaveDialog: async (options?: OpenSaveDialogOptions): Promise<string> =>
                this.openNewProjectSaveDialog(options?.templateId),
            createProject: async (projectPath: string, options: CreateProjectOptions): Promise<boolean> => {
                if (this.isBrowserOnlyMode()) return false;
                try {
                    return await this.getProjectService()!.createProject(projectPath, options);
                } catch {
                    return false;
                }
            },
            getGitRemotes: async (projectPath: string): Promise<GitWorkspaceInfo | undefined> => {
                if (this.isBrowserOnlyMode()) return undefined;
                return this.getProjectService()!.getGitRemotes(projectPath);
            },
            isGitInstalled: async (): Promise<boolean> => {
                if (this.isBrowserOnlyMode()) return false;
                return this.getProjectService()!.isGitInstalled();
            },
            getRecentProjects: async (): Promise<RecentProject[]> => this.getRecentLvglProjects(),
            getOpenedProject: async (): Promise<string | null> => this.getCurrentWorkspaceRootPath() ?? null,
            saveOpenedProject: async (projectPath: string | null): Promise<void> =>
                this.openWorkspaceProject(projectPath),
            shortenPath: (path: string): string => this.shortenProjectPath(path),
            getStoredAuthState: async (): Promise<AuthState> => this.getAuthStorage().getAuthState(),
            saveStoredAuthState: async (authState: AuthState): Promise<void> =>
                this.getAuthStorage().saveAuthState(authState),
            clearStoredAuthState: async (): Promise<void> => this.getAuthStorage().clearAuthState(),
            executeCommand: async (commandId: string): Promise<void> => {
                await this.commandRegistry.executeCommand(commandId);
            },
            runContextMenuAction: async (action: string, path: string, name?: string): Promise<Result> =>
                this.runContextMenuAction(action, path, name)
        };

        this.api = window._platformApi instanceof TheiaApi ? window._platformApi : new TheiaApi();
        this.api.setDelegates(delegates);
        window._platformApi = this.api;
        new LicenseService(this.api, isE2EAuthBypassBuild());
    }

    protected getProjectService(): RpcProxy<ITheiaProjectServiceServer> | undefined {
        if (!this.projectService) {
            console.debug('[Theia-API] Setting up connection to project service...');
            this.projectService = this.connectionProvider.createProxy<ITheiaProjectServiceServer>(
                TheiaProjectServicePath,
                this.projectServiceWatcher.getTheiaProjectServiceClient()
            );
        }
        return this.projectService;
    }

    protected getAuthStorage(): RpcProxy<ITheiaAuthStorageServer> {
        if (!this.authStorage) {
            this.authStorage = this.connectionProvider.createProxy<ITheiaAuthStorageServer>(TheiaAuthStoragePath);
        }
        return this.authStorage;
    }

    protected attachProjectServiceEventLogger(): void {
        this.toDispose.push(
            this.projectServiceWatcher.onProjectServiceEvent((event: ProjectServiceEvent) => {
                if (event.type === 'toolchain-setup-progress') {
                    this.api?.emitToolchainSetupProgress(event.progress);
                }
                const outputMessage: string | undefined = this.formatProjectServiceEventForOutput(event);
                if (outputMessage) this.emitOutputLog(outputMessage, this.outputSourceForProjectServiceEvent(event));
                void this.notifyProjectServiceEvent(event);
            })
        );
    }

    protected attachProjectServiceMessageBoxHandler(): void {
        this.projectServiceWatcher.setProjectServiceMessageBoxHandler((options: ProjectServiceMessageBoxOptions) =>
            this.showProjectServiceMessageBox(options)
        );
        this.toDispose.push(
            Disposable.create(() => {
                this.projectServiceWatcher.setProjectServiceMessageBoxHandler(undefined);
            })
        );
    }

    protected async showProjectServiceMessageBox(options: ProjectServiceMessageBoxOptions): Promise<number> {
        const actions: string[] = options.buttons;
        const fallbackResponse: number = options.cancelId ?? options.defaultId ?? actions.length - 1;
        const message: string = options.message;

        if (actions.length === 2) {
            const accepted: boolean =
                (await new ConfirmDialog({
                    title: options.title,
                    msg: message,
                    ok: actions[0],
                    cancel: actions[1]
                }).open()) === true;
            return accepted ? 0 : 1;
        }

        if (actions.length === 3) {
            const installAccepted: boolean =
                (await new ConfirmDialog({
                    title: options.title,
                    msg: message,
                    ok: actions[0],
                    cancel: actions[1]
                }).open()) === true;
            if (installAccepted) return 0;

            return 1;
        }

        const action: string | undefined =
            options.type === 'warning'
                ? await this.messageService.warn(`${options.title}: ${message}`, ...actions)
                : await this.messageService.info(`${options.title}: ${message}`, ...actions);
        const response: number = action ? actions.indexOf(action) : -1;
        return response >= 0 ? response : fallbackResponse;
    }

    protected formatProjectServiceEventForOutput(event: ProjectServiceEvent): string | undefined {
        switch (event.type) {
            case 'started':
                return `[LVGL] ${event.operation} started`;
            case 'progress':
                return `[LVGL] ${event.message}`;
            case 'completed':
                return `[LVGL] ${event.operation} completed`;
            case 'failed':
                return `[LVGL] ${event.operation} failed: ${event.message}`;
            case 'toolchain-setup-progress':
                return undefined;
        }
    }

    protected async notifyProjectServiceEvent(event: ProjectServiceEvent): Promise<void> {
        switch (event.type) {
            case 'started':
                await this.ensureProjectServiceProgress(
                    this.projectServiceProgressKey(event),
                    this.formatProjectServiceStartMessage(event.operation)
                );
                return;
            case 'progress':
                await this.reportProjectServiceProgress(event);
                return;
            case 'completed': {
                this.finishProjectServiceProgress(this.projectServiceProgressKey(event));
                const message: string = this.formatProjectServiceCompletionMessage(event.operation, event.result);
                if (event.result.success) {
                    this.showSuccessNotification(message);
                } else {
                    void this.messageService.error(message, { timeout: NOTIFICATION_TIMEOUT_MS });
                }
                return;
            }
            case 'failed':
                this.finishProjectServiceProgress(this.projectServiceProgressKey(event));
                void this.messageService.error(
                    this.formatProjectServiceFailureMessage(event.operation, event.message),
                    {
                        timeout: NOTIFICATION_TIMEOUT_MS
                    }
                );
                return;
            case 'toolchain-setup-progress':
                return;
        }
    }

    protected projectServiceProgressKey(event: ProjectServiceEvent): string {
        return event.requestId ?? `${event.operation}:${event.projectPath}`;
    }

    protected async ensureProjectServiceProgress(key: string, text: string): Promise<Progress> {
        const existing: Progress | undefined = this.projectServiceProgressByKey.get(key);
        if (existing) return existing;

        const starting: Promise<Progress> | undefined = this.projectServiceProgressStarting.get(key);
        if (starting) return starting;

        const promise: Promise<Progress> = this.messageService
            .showProgress({ text, options: { cancelable: false } })
            .then((progress: Progress) => {
                this.projectServiceProgressByKey.set(key, progress);
                this.projectServiceProgressStarting.delete(key);
                return progress;
            });
        this.projectServiceProgressStarting.set(key, promise);
        return promise;
    }

    protected async reportProjectServiceProgress(
        event: Extract<ProjectServiceEvent, { type: 'progress' }>
    ): Promise<void> {
        const key: string = this.projectServiceProgressKey(event);
        const progress: Progress = await this.ensureProjectServiceProgress(
            key,
            this.formatProjectServiceStartMessage(event.operation)
        );
        const percent: number | undefined = event.percent ?? this.parsePercentFromProgressMessage(event.message);
        const message: string = this.stripPercentPrefix(event.message) || event.message;
        progress.report({
            message,
            ...(percent !== undefined ? { work: { done: percent, total: 100 } } : {})
        });
    }

    protected finishProjectServiceProgress(key: string): void {
        const progress: Progress | undefined = this.projectServiceProgressByKey.get(key);
        if (progress) {
            progress.cancel();
            this.projectServiceProgressByKey.delete(key);
        }
        this.projectServiceProgressStarting.delete(key);
    }

    protected clearProjectServiceProgress(): void {
        for (const key of [...this.projectServiceProgressByKey.keys()]) {
            this.finishProjectServiceProgress(key);
        }
        this.projectServiceProgressStarting.clear();
    }

    protected parsePercentFromProgressMessage(message: string): number | undefined {
        const match: RegExpMatchArray | null = message.match(/\[\s*(\d+)%\s*\]/);
        if (!match) return undefined;
        return Number(match[1]);
    }

    protected stripPercentPrefix(message: string): string {
        return message.replace(/\[\s*\d+%\s*\]\s*/, '').trim();
    }

    protected formatProjectServiceStartMessage(
        operation: Extract<ProjectServiceEvent, { type: 'started' }>['operation']
    ): string {
        switch (operation) {
            case 'generate':
                return 'Generating code...';
            case 'compile':
                return 'Compiling project...';
            case 'clean':
                return 'Cleaning project...';
        }
    }

    protected formatProjectServiceCompletionMessage(
        operation: Extract<ProjectServiceEvent, { type: 'completed' }>['operation'],
        result: Result
    ): string {
        if (!result.success) {
            return this.formatProjectServiceFailureMessage(operation, result.error ?? `${operation} failed`);
        }
        switch (operation) {
            case 'generate':
                return 'Code generated successfully';
            case 'compile':
                return 'Project compiled successfully';
            case 'clean':
                return 'Project cleaned successfully';
        }
    }

    protected formatProjectServiceFailureMessage(
        operation: Extract<ProjectServiceEvent, { type: 'failed' }>['operation'],
        message: string
    ): string {
        return operation === 'clean' ? `Clean failed: ${message}` : `Build failed: ${message}`;
    }

    protected outputSourceForProjectServiceEvent(event: ProjectServiceEvent): OutputLogSource {
        return event.operation === 'generate' ? 'export' : 'runtime';
    }

    protected emitOutputLog(message: string, source: OutputLogSource = 'lvgl'): void {
        window.dispatchEvent(new CustomEvent(LVGL_LOG, { detail: { message, source } }));
    }

    protected attachFileChangeBridge(): void {
        this.toDispose.push(
            this.fileService.onDidFilesChange((event: FileChangesEvent) => {
                void this.handleFileChanges(event);
            })
        );
    }

    protected async handleFileChanges(event: FileChangesEvent): Promise<void> {
        if (!this.api) return;

        for (const change of event.changes) {
            console.debug(`[Theia-API] handleFileChanges ${change.resource} (${change.type})`);
            const fileEvent: LvglFileEvent | undefined = this.toLvglFileEvent(change.type);
            if (!fileEvent) continue;

            // Browser OPFS watch events can arrive with relative paths or a `/user` prefix, while the
            // rest of the editor expects paths under the opened workspace root.
            const changedPath: string = this.normalizeProjectPathForFileService(change.resource.path.toString());
            if (this.fileChangeSuppressionService.isSuppressed(changedPath)) continue;
            if (!this.isCurrentWorkspacePath(changedPath)) continue;

            const shouldEmit: boolean = await this.shouldEmitFileChange(fileEvent, changedPath);
            if (!shouldEmit) continue;

            this.api.emitFileSystemChange(fileEvent, changedPath);
            this.scheduleSchemaHotReload(changedPath);
            if (fileEvent === 'add' || fileEvent === 'unlink') {
                this.scheduleWorkspaceDirectoryRefresh(changedPath);
            }
        }
    }

    protected async shouldEmitFileChange(fileEvent: LvglFileEvent, changedPath: string): Promise<boolean> {
        if (fileEvent === 'unlink') return true;
        if (changedPath.toLowerCase().endsWith(XML_FILE_EXTENSION)) {
            try {
                // XML listeners reload schemas/translations immediately, so only broadcast once the
                // saved file is readable through Theia's FileService.
                await this.readTextFile(changedPath);
                return true;
            } catch {
                console.error(`[Theia-API] Ignoring unreadable XML file change event for ${changedPath}.`);
                return false;
            }
        }

        return this.canResolveFileChangePath(changedPath);
    }

    protected async canResolveFileChangePath(changedPath: string): Promise<boolean> {
        try {
            await this.fileService.resolve(this.toFileUri(changedPath));
            return true;
        } catch {
            console.debug(`[Theia-API] Ignoring unresolved file change event for ${changedPath}.`);
            return false;
        }
    }

    protected async readTextFile(filePath: string): Promise<string> {
        const content: TextFileContent = await this.fileService.read(this.toFileUri(filePath));
        return content.value;
    }

    protected async readBinaryFile(filePath: string): Promise<Uint8Array> {
        const content: FileContent = await this.fileService.readFile(this.toFileUri(filePath));
        return content.value.buffer.slice();
    }

    protected attachFileOperationBridge(): void {
        this.toDispose.push(
            this.fileService.onDidRunOperation((event: FileOperationEvent) => {
                if (!this.api) return;

                const operation: number = event.operation;

                if (operation === THEIA_FILE_OPERATION_MOVE && event.target) {
                    const sourcePath: string = this.normalizeProjectPathForFileService(event.resource.path.toString());
                    const targetPath: string = this.normalizeProjectPathForFileService(
                        event.target.resource.path.toString()
                    );
                    const isSourceSuppressed: boolean = this.fileChangeSuppressionService.isSuppressed(sourcePath);
                    const isTargetSuppressed: boolean = this.fileChangeSuppressionService.isSuppressed(targetPath);
                    const isSourceInWorkspace: boolean = this.isCurrentWorkspacePath(sourcePath);
                    const isTargetInWorkspace: boolean = this.isCurrentWorkspacePath(targetPath);

                    if (isSourceInWorkspace && !isSourceSuppressed) this.api.emitFileSystemChange('unlink', sourcePath);
                    if (isTargetInWorkspace && !isTargetSuppressed) this.api.emitFileSystemChange('add', targetPath);
                    if (isSourceInWorkspace && !isSourceSuppressed) this.scheduleSchemaHotReload(sourcePath);
                    if (isTargetInWorkspace && !isTargetSuppressed) this.scheduleSchemaHotReload(targetPath);
                    if ((isSourceInWorkspace && !isSourceSuppressed) || (isTargetInWorkspace && !isTargetSuppressed)) {
                        this.scheduleWorkspaceDirectoryRefresh(
                            isTargetInWorkspace && !isTargetSuppressed ? targetPath : sourcePath
                        );
                    }
                    return;
                }

                if (operation === THEIA_FILE_OPERATION_CREATE && event.target) {
                    const targetPath: string = this.normalizeProjectPathForFileService(
                        event.target.resource.path.toString()
                    );
                    if (!this.isCurrentWorkspacePath(targetPath)) return;
                    if (this.fileChangeSuppressionService.isSuppressed(targetPath)) return;

                    this.api.emitFileSystemChange('add', targetPath);
                    this.scheduleSchemaHotReload(targetPath);
                    this.scheduleWorkspaceDirectoryRefresh(targetPath);
                    return;
                }

                if (operation === THEIA_FILE_OPERATION_DELETE) {
                    const deletedPath: string = this.normalizeProjectPathForFileService(event.resource.path.toString());
                    if (!this.isCurrentWorkspacePath(deletedPath)) return;
                    if (this.fileChangeSuppressionService.isSuppressed(deletedPath)) return;

                    this.api.emitFileSystemChange('unlink', deletedPath);
                    this.scheduleSchemaHotReload(deletedPath);
                    this.scheduleWorkspaceDirectoryRefresh(deletedPath);
                }
            })
        );
    }

    protected scheduleSchemaHotReload(changedPath: string): void {
        if (!this.isSchemaHotReloadPath(changedPath)) return;

        this.clearSchemaHotReloadTimeout();
        this.schemaHotReloadTimeout = setTimeout(() => {
            this.schemaHotReloadTimeout = undefined;
            void this.hotReloadCurrentProjectSchema(changedPath);
        }, SCHEMA_HOT_RELOAD_DEBOUNCE_MS);
    }

    protected clearSchemaHotReloadTimeout(): void {
        if (!this.schemaHotReloadTimeout) return;
        clearTimeout(this.schemaHotReloadTimeout);
        this.schemaHotReloadTimeout = undefined;
    }

    protected isSchemaHotReloadPath(changedPath: string): boolean {
        if (changedPath.toLowerCase().endsWith(XML_FILE_EXTENSION)) return true;

        const workspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (!workspaceRootPath) return false;

        const normalizedChangedPath: string = normalizeFilePathForComparison(changedPath);
        const normalizedWorkspaceRootPath: string = normalizeFilePathForComparison(workspaceRootPath);
        if (!normalizedChangedPath.startsWith(`${normalizedWorkspaceRootPath}/`)) return false;

        const relativePath: string = normalizedChangedPath.slice(normalizedWorkspaceRootPath.length + 1);
        return (
            relativePath === 'components' ||
            relativePath.startsWith('components/') ||
            relativePath === 'screens' ||
            relativePath.startsWith('screens/') ||
            relativePath === 'lib' ||
            relativePath.startsWith('lib/')
        );
    }

    protected scheduleWorkspaceDirectoryRefresh(changedPath: string): void {
        if (!this.api) return;

        console.debug(`[Theia-API] scheduleWorkspaceDirectoryRefresh ${changedPath}`);

        const workspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (!workspaceRootPath) return;

        const normalizedChangedPath: string = normalizeFilePathForComparison(changedPath);
        const isInsideWorkspace: boolean =
            normalizedChangedPath === workspaceRootPath || normalizedChangedPath.startsWith(`${workspaceRootPath}/`);
        if (!isInsideWorkspace) return;

        this.clearDirectoryRefreshTimeout();
        this.directoryRefreshTimeout = setTimeout(() => {
            this.directoryRefreshTimeout = undefined;
            void this.emitWorkspaceDirectoryRefresh(workspaceRootPath);
        }, DIRECTORY_REFRESH_DEBOUNCE_MS);
    }

    protected clearDirectoryRefreshTimeout(): void {
        if (!this.directoryRefreshTimeout) return;
        clearTimeout(this.directoryRefreshTimeout);
        this.directoryRefreshTimeout = undefined;
    }

    protected async emitWorkspaceDirectoryRefresh(projectPath: string): Promise<void> {
        if (!this.api) return;

        try {
            console.debug(`[Theia-API] refreshing directory data for ${projectPath}`);
            const directoryData: DirectoryData | null = await this.api.getDirectoryData(projectPath);
            if (directoryData) {
                this.api.emitRefreshDirectory(directoryData);
                return;
            }
            await this.closeMissingWorkspace(projectPath);
        } catch (error: unknown) {
            console.error('[Theia-API] Failed to refresh project directory', error);
            await this.closeMissingWorkspace(projectPath);
        }
    }

    protected async closeMissingWorkspace(projectPath: string): Promise<void> {
        const currentWorkspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (currentWorkspaceRootPath !== normalizeFilePathForComparison(projectPath)) return;

        try {
            const stat: FileStat = await this.fileService.resolve(this.toFileUri(projectPath));
            if (stat.isDirectory) return;
        } catch (error: unknown) {
            const isNotFoundError = this.isFileNotFoundError(error);
            if (!isNotFoundError) {
                console.warn(
                    '[Theia-API] Non-fatal error during workspace verification, keeping workspace open',
                    error
                );
                return;
            }
        }

        this.clearDirectoryRefreshTimeout();
        // Prefer in-place close: WorkspaceService.close() reloads the whole window in Theia 1.69.
        const closed: boolean = await this.closeWorkspaceInPlace().catch((error: unknown): boolean => {
            console.warn('[Theia-API] Failed to close removed workspace', error);
            return false;
        });
        if (!closed) return;

        useFileStore.getState().clearWorkspace();
        await this.messageService.warn('The opened project folder was removed. The workspace has been closed.');
    }

    protected isFileNotFoundError(error: unknown): boolean {
        if (!error || typeof error !== 'object') return false;

        const err = error as { code?: string; name?: string; message?: string };

        if (err.code === 'ENOENT' || err.code === 'FileNotFound') return true;
        if (err.name?.includes('NotFound')) return true;

        const message = err.message?.toLowerCase() || '';
        return message.includes('not found') || message.includes('does not exist') || message.includes('no such file');
    }

    protected async toFsNode(stat: FileStat): Promise<FSNode> {
        const resolved: FileStat = stat.isDirectory
            ? await this.fileService.resolve(this.toFileUri(stat.resource.path.toString()))
            : stat;
        const path: string = this.normalizeProjectPathForFileService(resolved.resource.path.toString());
        const children: FSNode[] | undefined =
            resolved.isDirectory && resolved.children
                ? await Promise.all(resolved.children.map((child: FileStat) => this.toFsNode(child)))
                : undefined;

        return {
            name: resolved.name,
            path,
            type: resolved.isDirectory ? 'directory' : 'file',
            size: resolved.size,
            modifiedTime: resolved.mtime ? new Date(resolved.mtime) : undefined,
            createdTime: resolved.ctime ? new Date(resolved.ctime) : undefined,
            extension: resolved.isFile ? this.extension(resolved.name) : undefined,
            children
        };
    }

    protected toFileUri(path: string): URI {
        const normalizedPath: string = this.normalizeProjectPathForFileService(path);
        return normalizedPath.startsWith('file:')
            ? new URI(normalizedPath)
            : URI.fromFilePath(normalizeWindowsDrivePath(normalizedPath));
    }

    protected normalizeProjectPathForFileService(path: string): string {
        if (!this.isBrowserOnlyMode() || path.startsWith('file:') || this.isBrowserResourcesPath(path)) {
            return path;
        }

        // Shared UI code is platform-agnostic and may pass either workspace-relative paths
        // (`screens/main.xml`) or paths echoed by Theia's browser OPFS layer (`/user/...`).
        // Normalize them here so schema, preview, translations, and file-store code can keep
        // using the same PlatformAPI contract in Electron and Browser.
        const normalizedPath: string = normalizeFilePathForComparison(normalizeWindowsDrivePath(path));
        const workspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (!workspaceRootPath) return normalizedPath;

        if (this.isPathInsideOrEqual(normalizedPath, workspaceRootPath)) {
            return normalizedPath;
        }

        // Some OPFS save events are reported under `/user`; strip it only when the remaining path
        // points into the current workspace, so unrelated absolute paths are left untouched.
        const withoutBrowserUserPrefix: string = normalizedPath.replace(/^\/user(?=\/)/, '');
        if (this.isPathInsideOrEqual(withoutBrowserUserPrefix, workspaceRootPath)) {
            return withoutBrowserUserPrefix;
        }

        // Relative file events and UI paths should be interpreted relative to the opened workspace.
        if (!normalizedPath.startsWith('/')) {
            return normalizeFilePathForComparison(joinFsPath(workspaceRootPath, normalizedPath));
        }

        return normalizedPath;
    }

    protected getCurrentWorkspaceRootPath(): string | undefined {
        const rootPath: string | undefined = this.workspaceService.tryGetRoots()[0]?.resource.path.toString();
        if (!rootPath) return undefined;

        return normalizeFilePathForComparison(normalizeWindowsDrivePath(rootPath));
    }

    protected isPathInsideOrEqual(path: string, rootPath: string): boolean {
        return path === rootPath || path.startsWith(`${rootPath}/`);
    }

    protected isCurrentWorkspacePath(path: string): boolean {
        const workspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (!workspaceRootPath) return false;

        const normalizedPath: string = normalizeFilePathForComparison(path);
        return this.isPathInsideOrEqual(normalizedPath, workspaceRootPath);
    }

    // TODO: use standard Theia path APIs for this (make sure they normalize for Windows)
    protected dirname(path: string): string {
        const normalized: string = normalizeWindowsDrivePath(path);
        const index: number = normalized.lastIndexOf('/');
        return index > 0 ? normalized.slice(0, index) : normalized;
    }

    protected extension(name: string): string | undefined {
        const index: number = name.lastIndexOf('.');
        return index >= 0 ? name.slice(index) : undefined;
    }

    protected toLvglFileEvent(type: number): LvglFileEvent | undefined {
        switch (type) {
            case 1:
                return 'add';
            case 0:
                return 'change';
            case 2:
                return 'unlink';
            default:
                return undefined;
        }
    }

    protected detectHostPlatform(): string {
        const userAgent: string = navigator.userAgent.toLowerCase();
        if (userAgent.includes('mac')) return 'darwin';
        if (userAgent.includes('win')) return 'win32';
        return 'linux';
    }

    protected async resolveResourcesSourcePath(): Promise<string> {
        if (this.isBrowserOnlyMode()) return this.resolveBrowserResourcesPath();

        const projectService: RpcProxy<ITheiaProjectServiceServer> | undefined = this.getProjectService();
        if (!projectService) {
            console.warn('[Theia-API] Project service is not available; using browser resources path.');
            return this.resolveBrowserResourcesPath();
        }

        try {
            return normalizeWindowsDrivePath(await projectService.getResourcesSourcePath());
        } catch (error: unknown) {
            console.warn(
                '[Theia-API] Failed to resolve resources from project service, using browser resources.',
                error
            );
            return this.resolveBrowserResourcesPath();
        }
    }

    private async resolveResourcesPath(): Promise<string> {
        const projectService: RpcProxy<ITheiaProjectServiceServer> | undefined = this.getProjectService();
        if (!projectService) {
            console.warn('[Theia-API] Project service is not available; using browser resources path.');
            return this.resolveBrowserResourcesPath();
        }

        try {
            return normalizeWindowsDrivePath(await projectService.getResourcesPath());
        } catch (error: unknown) {
            console.warn(
                '[Theia-API] Failed to resolve resources from project service, using browser resources.',
                error
            );
            return this.resolveBrowserResourcesPath();
        }
    }

    protected resolveBrowserResourcesPath(): string {
        if (window.location.protocol !== 'file:') {
            const val: string = TheiaPlatformApiContribution.BROWSER_RESOURCES_ROOT;
            console.debug(`[Theia-API] Resolved browser resources path: ${val} (using relative path)`);
            return val;
        }

        const frontendDir: string = decodeURIComponent(new URL('.', window.location.href).pathname);
        const val: string = normalizeWindowsDrivePath(
            URI.fromFilePath(normalizeWindowsDrivePath(frontendDir)).resolve('@lved-runtime-resources').path.toString()
        );
        console.debug(`[Theia-API] Resolved browser resources path: ${val} (using absolute path)`);
        return val;
    }

    protected isBrowserResourcesPath(path: string): boolean {
        return (
            window.location.protocol !== 'file:' &&
            path.replace(/\\/g, '/').startsWith(TheiaPlatformApiContribution.BROWSER_RESOURCES_ROOT)
        );
    }

    protected uriToProjectPath(uriString: string | undefined): string | undefined {
        if (!uriString) return undefined;
        return normalizeWindowsDrivePath(new URI(uriString).path.toString());
    }

    protected shortenProjectPath(path: string): string {
        const normalized: string = normalizeWindowsDrivePath(path);
        if (this.homePath && normalized.startsWith(this.homePath)) {
            const suffix: string = normalized.slice(this.homePath.length).replace(/^\//, '');
            return suffix ? `~/${suffix}` : '~';
        }
        return normalized;
    }

    protected wrapDirectoryData(dirPath: string, files: FSNode[]): DirectoryData {
        const normalized: string = normalizeWindowsDrivePath(dirPath);
        const name: string = normalized.split('/').filter(Boolean).pop() ?? normalized;
        return {
            dirPath: normalized,
            files: [
                {
                    name,
                    path: normalized,
                    type: 'directory',
                    children: files
                }
            ]
        };
    }

    protected async getRecentLvglProjects(): Promise<RecentProject[]> {
        const storedProjects: StoredRecentProject[] = await this.getStoredRecentProjects();
        const projects: RecentProject[] = [];
        const keptProjects: StoredRecentProject[] = [];

        for (const storedProject of storedProjects) {
            if (!(await this.directoryExists(storedProject.path))) continue;

            projects.push(this.createRecentProject(storedProject.path, new Date(storedProject.lastOpened)));
            keptProjects.push(storedProject);
        }

        if (keptProjects.length !== storedProjects.length) {
            await this.storageService.setData(LVGL_RECENT_PROJECTS_STORAGE_KEY, keptProjects);
        }

        const knownPaths: Set<string> = new Set<string>(
            projects.map((project: RecentProject): string => normalizeFilePathForComparison(project.path))
        );
        const uris: string[] = await this.workspaceService.recentWorkspaces();
        for (const uriString of uris) {
            const path: string | undefined = this.uriToProjectPath(uriString);
            if (!path) continue;

            const normalizedPath: string = normalizeFilePathForComparison(path);
            if (knownPaths.has(normalizedPath)) continue;

            try {
                const stat = await this.fileService.resolve(this.toFileUri(path));
                if (!stat.isDirectory) continue;
            } catch {
                continue;
            }

            projects.push(this.createRecentProject(path, new Date(0)));
            knownPaths.add(normalizedPath);
        }

        return projects
            .sort((a: RecentProject, b: RecentProject): number => b.lastOpened.getTime() - a.lastOpened.getTime())
            .slice(0, MAX_RECENT_PROJECTS);
    }

    protected async rememberRecentProject(projectPath: string): Promise<void> {
        const normalizedProjectPath: string = normalizeFilePathForComparison(normalizeWindowsDrivePath(projectPath));
        const projects: StoredRecentProject[] = await this.getStoredRecentProjects();
        const nextProjects: StoredRecentProject[] = [
            {
                path: normalizedProjectPath,
                lastOpened: new Date().toISOString()
            },
            ...projects.filter(
                (project: StoredRecentProject): boolean =>
                    normalizeFilePathForComparison(project.path) !== normalizedProjectPath
            )
        ].slice(0, MAX_RECENT_PROJECTS);

        await this.storageService.setData(LVGL_RECENT_PROJECTS_STORAGE_KEY, nextProjects);
    }

    protected async getStoredRecentProjects(): Promise<StoredRecentProject[]> {
        const storedValue: unknown = await this.storageService.getData<unknown>(LVGL_RECENT_PROJECTS_STORAGE_KEY, []);
        if (!Array.isArray(storedValue)) return [];

        return storedValue.filter((value: unknown): value is StoredRecentProject => this.isStoredRecentProject(value));
    }

    protected isStoredRecentProject(value: unknown): value is StoredRecentProject {
        if (!value || typeof value !== 'object') return false;

        const record: Record<string, unknown> = value as Record<string, unknown>;
        if (typeof record.path !== 'string' || record.path.length === 0) return false;
        if (typeof record.lastOpened !== 'string') return false;

        return Number.isFinite(Date.parse(record.lastOpened));
    }

    protected createRecentProject(projectPath: string, lastOpened: Date): RecentProject {
        const normalizedPath: string = normalizeWindowsDrivePath(projectPath);
        const name: string = normalizedPath.split('/').filter(Boolean).pop() ?? normalizedPath;
        return {
            path: normalizedPath,
            name,
            shortenedPath: this.shortenProjectPath(normalizedPath),
            lastOpened
        };
    }

    protected async selectProjectDirectory(projectPath?: string): Promise<DirectoryData | null> {
        if (this.isBrowserOnlyMode() && !projectPath) {
            window.dispatchEvent(new CustomEvent(ONLINE_PREVIEW_SHOW_LAUNCHER_EVENT));
            return null;
        }

        let selectedPath: string | undefined = projectPath ? normalizeWindowsDrivePath(projectPath) : undefined;

        if (!selectedPath) {
            const selectedUri = await this.fileDialogService.showOpenDialog({
                title: 'Open UI Project',
                openLabel: 'Open',
                canSelectFiles: false,
                canSelectFolders: true,
                canSelectMany: false
            });
            if (!selectedUri) return null;
            selectedPath = this.uriToProjectPath(selectedUri.toString());
        }

        if (!selectedPath) return null;

        const directoryData: DirectoryData | null = await this.delegatesGetDirectoryData(selectedPath);
        if (!directoryData) return null;

        const wrapped: DirectoryData = this.wrapDirectoryData(directoryData.dirPath, directoryData.files as FSNode[]);
        const resolvedProject: ResolvedLvglProjectDirectory = resolveLvglProjectDirectory(wrapped);
        if (!resolvedProject.success || !resolvedProject.editorPath) return wrapped;
        if (
            normalizeFilePathForComparison(resolvedProject.editorPath) ===
            normalizeFilePathForComparison(directoryData.dirPath)
        ) {
            return wrapped;
        }

        const editorDirectoryData: DirectoryData | null = await this.delegatesGetDirectoryData(
            resolvedProject.editorPath
        );
        if (!editorDirectoryData) return null;

        return this.wrapDirectoryData(editorDirectoryData.dirPath, editorDirectoryData.files as FSNode[]);
    }

    protected async openWorkspaceProject(projectPath: string | null): Promise<void> {
        if (!projectPath) {
            // Logout / launcher flows need the workspace cleared without reloading the window.
            // Theia's default WorkspaceService.close() always calls reloadWindow('').
            await this.closeWorkspaceInPlace();
            return;
        }

        const normalizedProjectPath: string = normalizeFilePathForComparison(normalizeWindowsDrivePath(projectPath));
        if (this.getCurrentWorkspaceRootPath() === normalizedProjectPath) {
            await this.trustCurrentWorkspaceRoot();
            await this.rememberRecentProject(normalizedProjectPath);
            return;
        }

        await this.setWorkspaceInPlace(normalizedProjectPath).catch((error: unknown): void => {
            console.warn('[Theia-API] Failed to update workspace for opened project', error);
        });
    }

    /**
     * Closes every closable tab in the main area, regardless of which open handler created it.
     * editorManager.closeAll() only closes text-editor widgets it tracks itself, so it misses
     * tabs opened by other handlers (e.g. ImageOpenHandler's image previews).
     */
    protected async closeAllMainAreaTabs(): Promise<void> {
        await this.shell.closeTabs('main', (title): boolean => title.closable);
    }

    protected hasOpenMainAreaTabs(): boolean {
        return this.shell.mainAreaTabBars.some((tabBar): boolean =>
            tabBar.titles.some((title): boolean => title.closable)
        );
    }

    /**
     * Clears the current workspace without reloading the frontend window.
     * Use this instead of WorkspaceService.close() for logout and similar UI transitions.
     */
    protected async closeWorkspaceInPlace(): Promise<boolean> {
        const workspaceService: InPlaceWorkspaceService = this.workspaceService as unknown as InPlaceWorkspaceService;
        if (!workspaceService.opened) return true;

        await this.closeAllMainAreaTabs().catch((error: unknown): void => {
            console.warn('[Theia-API] Failed to close open tabs while closing workspace', error);
        });
        if (this.hasOpenMainAreaTabs()) return false;

        await this.closeWorkspaceScopedBottomPanels();
        this.navigationLocationService.clearHistory();

        if (workspaceService.closeInCurrentWindow) {
            await workspaceService.closeInCurrentWindow();
            return true;
        }

        // WorkspaceService.close() reloads the frontend in Theia 1.69.
        await workspaceService.setWorkspace(undefined);
        return true;
    }

    protected async setWorkspaceInPlace(projectPath: string): Promise<void> {
        const workspaceStat: FileStat = await this.fileService.resolve(this.toFileUri(projectPath));

        console.debug(
            `[Theia-API] Setting workspace in place to ${projectPath} (${workspaceStat.resource.toString()})`
        );

        // Theia's addToTrustedFolders() only sees the current workspace, so trust the target before switching.
        await this.trustWorkspacePath(projectPath);

        // WorkspaceService.open(..., { preserveWindow: true }) reloads the frontend in Theia 1.69.
        await (this.workspaceService as unknown as InPlaceWorkspaceService).setWorkspace(workspaceStat);
        await this.trustCurrentWorkspaceRoot();

        // Do this to avoid opening tabs in the previous workspace will remain open after switching to a new workspace.
        useFileStore.getState().closeAllTabs();
        await this.closeAllMainAreaTabs();
        await this.closeWorkspaceScopedBottomPanels();

        // Clears the "recently opened" quick-open list and back/forward history from the previous workspace.
        // We cannot open files from a different project in the current one, so not allowing the user to
        // navigate back to the previous workspace is safer than leaving them in a broken state.
        this.navigationLocationService.clearHistory();
        await this.prepareDebuggerForWorkspace(projectPath);
        await this.rememberRecentProject(projectPath);
    }

    protected async prepareDebuggerForWorkspace(projectPath: string): Promise<void> {
        if (!(await this.hasWorkspaceDebugConfiguration(projectPath))) return;

        const selected: boolean = await this.ensureDebugConfigurationSelected(projectPath);
        if (selected) return;

        void this.messageService.warn(
            'Debugger configuration is available, but it could not be selected automatically.'
        );
    }

    protected async hasWorkspaceDebugConfiguration(projectPath: string): Promise<boolean> {
        for (const debugConfigurationPath of this.getWorkspaceDebugConfigurationPaths(projectPath)) {
            if (await this.fileExists(debugConfigurationPath)) return true;
        }

        return false;
    }

    protected getWorkspaceDebugConfigurationPaths(projectPath: string): readonly string[] {
        return [joinFsPath(projectPath, '.theia', 'launch.json'), joinFsPath(projectPath, '.vscode', 'launch.json')];
    }

    protected async ensureDebugConfigurationSelected(projectPath: string): Promise<boolean> {
        const workspaceUri: URI = this.toFileUri(projectPath).normalizePath();
        if (this.hasCurrentDebugConfigurationForWorkspace(workspaceUri)) return true;

        const debugSessionOptions: DebugSessionOptions | undefined =
            await this.readWorkspaceDebugSessionOptions(projectPath);
        if (!debugSessionOptions) return this.hasCurrentDebugConfigurationForWorkspace(workspaceUri);

        this.debugSessionOptionsByWorkspace.set(workspaceUri.toString(), debugSessionOptions);

        // If Theia's debug models are ready, this updates the toolbar selection too.
        this.debugConfigurationManager.current = debugSessionOptions;
        return true;
    }

    protected async readWorkspaceDebugSessionOptions(projectPath: string): Promise<DebugSessionOptions | undefined> {
        const workspaceFolderUri: string = this.toFileUri(projectPath).normalizePath().toString();

        for (const debugConfigurationPath of this.getWorkspaceDebugConfigurationPaths(projectPath)) {
            const launchConfigurationFile: LaunchConfigurationFile | undefined =
                await this.readLaunchConfigurationFile(debugConfigurationPath);
            const configuration: DebugConfiguration | undefined = launchConfigurationFile?.configurations[0];
            if (configuration) {
                return {
                    name: configuration.name,
                    configuration,
                    workspaceFolderUri
                };
            }
        }

        return undefined;
    }

    protected async readLaunchConfigurationFile(
        debugConfigurationPath: string
    ): Promise<LaunchConfigurationFile | undefined> {
        if (!(await this.fileExists(debugConfigurationPath))) return undefined;

        try {
            const content: TextFileContent = await this.fileService.read(this.toFileUri(debugConfigurationPath));
            const parsed: unknown = JSON.parse(content.value);
            return this.parseLaunchConfigurationFile(parsed);
        } catch (error: unknown) {
            console.warn(`[Theia-API] Failed to read debug configuration from ${debugConfigurationPath}`, error);
            return undefined;
        }
    }

    protected parseLaunchConfigurationFile(parsed: unknown): LaunchConfigurationFile | undefined {
        if (!this.isRecord(parsed)) return undefined;

        const configurations: unknown = parsed.configurations;
        if (!Array.isArray(configurations)) return undefined;

        const debugConfigurations: DebugConfiguration[] = [];
        for (const configuration of configurations) {
            if (DebugConfiguration.is(configuration)) debugConfigurations.push(configuration);
        }

        return { configurations: debugConfigurations };
    }

    protected isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }

    protected registerDebugStartFallbacks(): void {
        this.toDispose.push(
            this.commandRegistry.registerHandler(DebugCommands.START.id, {
                execute: (debugSessionOptions?: unknown): Promise<void> =>
                    this.startDebugWithFallback(false, debugSessionOptions)
            })
        );
        this.toDispose.push(
            this.commandRegistry.registerHandler(DebugCommands.START_NO_DEBUG.id, {
                execute: (debugSessionOptions?: unknown): Promise<void> =>
                    this.startDebugWithFallback(true, debugSessionOptions)
            })
        );
    }

    protected async startDebugWithFallback(noDebug: boolean, debugSessionOptions?: unknown): Promise<void> {
        const selectedOptions: DebugSessionOptions | undefined = DebugSessionOptions.is(debugSessionOptions)
            ? debugSessionOptions
            : (this.getCurrentWorkspaceDebugSessionOptions() ?? (await this.getFallbackDebugSessionOptions()));

        if (!selectedOptions) {
            await this.debugConfigurationManager.addConfiguration();
            return;
        }

        await this.debugSessionManager.start(this.withDebugMode(selectedOptions, noDebug));
    }

    protected getCurrentWorkspaceDebugSessionOptions(): DebugSessionOptions | undefined {
        const workspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        const currentDebugConfiguration: DebugSessionOptions | undefined = this.debugConfigurationManager.current;
        if (!workspaceRootPath || !currentDebugConfiguration?.workspaceFolderUri) return undefined;

        const workspaceUri: URI = this.toFileUri(workspaceRootPath).normalizePath();
        return this.isDebugSessionOptionsForWorkspace(currentDebugConfiguration, workspaceUri)
            ? currentDebugConfiguration
            : undefined;
    }

    protected async getFallbackDebugSessionOptions(): Promise<DebugSessionOptions | undefined> {
        const workspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        if (!workspaceRootPath) return undefined;

        const workspaceUri: string = this.toFileUri(workspaceRootPath).normalizePath().toString();
        const storedOptions: DebugSessionOptions | undefined = this.debugSessionOptionsByWorkspace.get(workspaceUri);
        if (storedOptions) return storedOptions;

        const debugSessionOptions: DebugSessionOptions | undefined =
            await this.readWorkspaceDebugSessionOptions(workspaceRootPath);
        if (debugSessionOptions) {
            this.debugSessionOptionsByWorkspace.set(workspaceUri, debugSessionOptions);
        }

        return debugSessionOptions;
    }

    protected withDebugMode(debugSessionOptions: DebugSessionOptions, noDebug: boolean): DebugSessionOptions {
        if (debugSessionOptions.configuration) {
            return {
                ...debugSessionOptions,
                configuration: {
                    ...debugSessionOptions.configuration,
                    noDebug
                }
            };
        }

        return {
            ...debugSessionOptions,
            noDebug
        };
    }

    protected hasCurrentDebugConfigurationForWorkspace(workspaceUri: URI): boolean {
        const currentDebugConfiguration: DebugSessionOptions | undefined = this.debugConfigurationManager.current;
        return currentDebugConfiguration
            ? this.isDebugSessionOptionsForWorkspace(currentDebugConfiguration, workspaceUri)
            : false;
    }

    protected isDebugSessionOptionsForWorkspace(debugSessionOptions: DebugSessionOptions, workspaceUri: URI): boolean {
        if (!debugSessionOptions.workspaceFolderUri) return false;

        try {
            const currentWorkspaceUri: URI = new URI(debugSessionOptions.workspaceFolderUri).normalizePath();
            return currentWorkspaceUri.isEqual(workspaceUri, !OS.backend.isWindows);
        } catch {
            return false;
        }
    }

    protected async trustWorkspacePath(projectPath: string): Promise<void> {
        if (this.isBrowserOnlyMode()) return;

        try {
            await this.addTrustedFolder(this.toFileUri(projectPath));
            this.workspaceTrustService.setWorkspaceTrust(true);
        } catch (error: unknown) {
            console.warn('[Theia-API] Failed to trust workspace before opening', error);
        }
    }

    protected async addTrustedFolder(uri: URI): Promise<void> {
        await this.preferences.ready;

        const currentFolders: string[] = this.preferences.get<string[]>(WORKSPACE_TRUST_TRUSTED_FOLDERS, []);
        const normalizedUri: URI = uri.normalizePath();
        const caseSensitive: boolean = !OS.backend.isWindows;
        const isTrusted: boolean = currentFolders.some((folder: string): boolean => {
            try {
                return new URI(folder).normalizePath().isEqualOrParent(normalizedUri, caseSensitive);
            } catch {
                return false;
            }
        });

        if (isTrusted) return;

        await this.preferences.set(
            WORKSPACE_TRUST_TRUSTED_FOLDERS,
            [...currentFolders, uri.toString()],
            PreferenceScope.User
        );
    }

    protected async trustCurrentWorkspaceRoot(): Promise<void> {
        if (this.isBrowserOnlyMode() || !this.getCurrentWorkspaceRootPath()) return;

        try {
            this.workspaceTrustService.setWorkspaceTrust(true);
            await this.workspaceTrustService.addToTrustedFolders();
        } catch (error: unknown) {
            console.warn('[Theia-API] Failed to trust current workspace', error);
        }
    }

    protected async closeWorkspaceScopedBottomPanels(): Promise<void> {
        // Build/terminal tabs can keep paths and logs from the previous workspace.
        // Keep Output available because validation/build diagnostics are written there.
        const bottomWidgets: Widget[] = this.shell.getWidgets('bottom');
        const closePromises: Promise<void>[] = bottomWidgets
            .filter((widget: Widget): boolean => !PRESERVED_BOTTOM_PANEL_WIDGET_IDS.has(widget.id))
            .map(async (widget: Widget): Promise<void> => {
                try {
                    await this.shell.closeWidget(widget.id);
                } catch (error: unknown) {
                    console.warn(`[Theia-API] Failed to close bottom panel ${widget.id}`, error);
                }
            });

        await Promise.all(closePromises);
    }

    protected async runContextMenuAction(action: string, itemPath: string, itemName?: string): Promise<Result> {
        const normalizedItemPath: string = this.normalizeProjectPathForFileService(itemPath);
        const workspaceRootPath: string | undefined = this.getCurrentWorkspaceRootPath();
        const isProjectRoot: boolean =
            !!workspaceRootPath && normalizeFilePathForComparison(normalizedItemPath) === workspaceRootPath;

        try {
            switch (action) {
                case 'rename': {
                    if (isProjectRoot) {
                        return { success: false, error: 'Cannot rename the project root directory' };
                    }
                    if (!itemName) {
                        return { success: false, error: 'No new name provided for rename operation' };
                    }
                    const newPath: string = normalizeFilePathForComparison(
                        joinFsPath(this.dirname(normalizedItemPath), itemName)
                    );
                    const itemStat: FileStat = await this.fileService.resolve(this.toFileUri(normalizedItemPath));
                    const isValidName: boolean = itemStat.isDirectory
                        ? isValidCIdentifier(itemName)
                        : !/[\\/]/.test(itemName) && isValidCIdentifierFilePath(itemName);
                    if (!isValidName) {
                        return this.rejectInvalidFileOrDirectoryName(newPath);
                    }
                    const newUri: URI = this.toFileUri(newPath);
                    // FileService#move silently renames the target to a non-colliding name instead of
                    // throwing when it already exists, so the existing-target check must happen here.
                    if (await this.fileService.exists(newUri)) {
                        return { success: false, error: `Target already exists: ${newPath}` };
                    }
                    await this.fileService.move(this.toFileUri(normalizedItemPath), newUri);
                    return { success: true };
                }
                case 'new-file': {
                    if (!itemName) return { success: false, error: 'No name provided for new file' };
                    const filePath: string = normalizeFilePathForComparison(joinFsPath(normalizedItemPath, itemName));
                    if (!isValidCIdentifierFilePath(filePath)) {
                        return this.rejectInvalidFileOrDirectoryName(filePath);
                    }
                    await this.fileService.create(this.toFileUri(filePath), '');
                    return { success: true };
                }
                case 'new-folder': {
                    if (!itemName) return { success: false, error: 'No name provided for new folder' };
                    const folderPath: string = normalizeFilePathForComparison(joinFsPath(normalizedItemPath, itemName));
                    const isSystemDirectory: boolean = SYSTEM_DIRECTORIES.has(itemName);
                    const isValid: boolean = isSystemDirectory
                        ? VALID_C_VAR_NAME.test(itemName)
                        : isValidCIdentifier(itemName);
                    if (!isValid) {
                        return this.rejectInvalidFileOrDirectoryName(folderPath);
                    }
                    await this.fileService.createFolder(this.toFileUri(folderPath));
                    return { success: true };
                }
                default:
                    console.warn(`[Theia-API] Unhandled context menu action: ${action}`);
                    return { success: false, error: `Unhandled operation: ${action}` };
            }
        } catch (error: unknown) {
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error)
            };
        }
    }

    protected async rejectInvalidFileOrDirectoryName(path: string): Promise<Result> {
        await this.messageService.error(INVALID_FILE_OR_DIRECTORY_VARIABLE_NAME_MESSAGE);
        return createInvalidFileOrDirectoryNameResult(path);
    }

    protected async delegatesGetDirectoryData(dirPath: string): Promise<DirectoryData | null> {
        const stat: FileStat = await this.fileService.resolve(this.toFileUri(dirPath));
        if (!stat.isDirectory) return null;
        return {
            dirPath,
            files: await Promise.all((stat.children ?? []).map((child: FileStat) => this.toFsNode(child)))
        };
    }

    protected async openDestinationDirectorySelector(defaultPath?: string): Promise<string> {
        let defaultFolder: FileStat | undefined;
        if (defaultPath) {
            try {
                defaultFolder = await this.fileService.resolve(this.toFileUri(defaultPath));
            } catch {
                defaultFolder = undefined;
            }
        }

        const selectedUri = await this.fileDialogService.showOpenDialog(
            {
                title: 'Select a Project Destination Folder',
                openLabel: 'Select as Project Destination',
                canSelectFiles: false,
                canSelectFolders: true,
                canSelectMany: false
            },
            defaultFolder
        );
        if (!selectedUri) return '';
        return this.uriToProjectPath(selectedUri.toString()) ?? '';
    }

    protected async openNewProjectSaveDialog(templateId?: ProjectTemplateId): Promise<string> {
        const selectedUri = await this.fileDialogService.showSaveDialog({
            title: 'Create UI Project',
            saveLabel: 'Create',
            inputValue: getProjectTemplateDefaultFolderName(templateId)
        });
        if (!selectedUri) return '';
        return this.uriToProjectPath(selectedUri.toString()) ?? '';
    }

    protected async getHttpDirectoryData(dirPath: string): Promise<DirectoryData | null> {
        const manifestDirectoryData: DirectoryData | null = await this.getManifestDirectoryData(dirPath);
        if (manifestDirectoryData) return manifestDirectoryData;

        const normalizedDir: string = dirPath.endsWith('/') ? dirPath : `${dirPath}/`;
        const response: Response = await fetch(normalizedDir);
        if (!response.ok) return null;

        const html: string = await response.text();
        const parser = new DOMParser();
        const doc: Document = parser.parseFromString(html, 'text/html');
        const anchors: HTMLAnchorElement[] = Array.from(doc.querySelectorAll('a'));

        const files: FSNode[] = anchors
            .map((anchor: HTMLAnchorElement) => anchor.getAttribute('href') || '')
            .filter(
                (href: string) =>
                    href &&
                    href !== '../' &&
                    !href.startsWith('?') &&
                    !href.startsWith('http://') &&
                    !href.startsWith('https://')
            )
            .map((href: string) => {
                const normalizedHref: string = href.replace(/^\.\//, '');
                const isDirectory: boolean = normalizedHref.endsWith('/');
                const name: string = decodeURIComponent(normalizedHref).replace(/\/$/, '');
                const fullPath: string = `${normalizedDir}${normalizedHref}`.replace(/\/$/, '');
                return {
                    name,
                    path: fullPath,
                    type: isDirectory ? 'directory' : 'file',
                    extension: isDirectory ? undefined : this.extension(name)
                } as FSNode;
            });

        return { dirPath: normalizedDir.replace(/\/$/, ''), files };
    }

    protected async getManifestDirectoryData(dirPath: string): Promise<DirectoryData | null> {
        const manifest: RuntimeResourcesManifest | null = await this.getRuntimeResourcesManifest();
        if (!manifest) return null;

        const normalizedDir: string = normalizeFilePathForComparison(dirPath);
        const files: FSNode[] | undefined = manifest[normalizedDir];
        if (!files) return null;

        return {
            dirPath: normalizedDir,
            files
        };
    }

    protected getRuntimeResourcesManifest(): Promise<RuntimeResourcesManifest | null> {
        if (!this.runtimeResourcesManifestPromise) {
            this.runtimeResourcesManifestPromise = this.fetchRuntimeResourcesManifest();
        }
        return this.runtimeResourcesManifestPromise;
    }

    protected async fetchRuntimeResourcesManifest(): Promise<RuntimeResourcesManifest | null> {
        const manifestUrl: string = `${TheiaPlatformApiContribution.BROWSER_RESOURCES_ROOT}/${RUNTIME_RESOURCES_MANIFEST_FILE_NAME}`;

        try {
            const response: Response = await fetch(manifestUrl);
            if (!response.ok) return null;

            const manifest: unknown = await response.json();
            if (!this.isRuntimeResourcesManifest(manifest)) return null;

            return manifest;
        } catch (error: unknown) {
            console.warn('[Theia-API] Failed to load runtime resources manifest.', error);
            return null;
        }
    }

    protected isRuntimeResourcesManifest(value: unknown): value is RuntimeResourcesManifest {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

        return Object.entries(value as Record<string, unknown>).every(
            ([directoryPath, files]: [string, unknown]): boolean =>
                typeof directoryPath === 'string' &&
                Array.isArray(files) &&
                files.every((file: unknown): boolean => this.isManifestNode(file))
        );
    }

    protected isManifestNode(value: unknown): value is FSNode {
        if (!value || typeof value !== 'object') return false;

        const node: Record<string, unknown> = value as Record<string, unknown>;
        return (
            typeof node.name === 'string' &&
            typeof node.path === 'string' &&
            (node.type === 'file' || node.type === 'directory')
        );
    }
}
